import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Filter, X } from 'lucide-react';

const ESTADOS = ['pendiente', 'validada', 'error_ocr', 'procesando', 'borrador'];
const ORIGENES = ['email', 'manual', 'api'];

export default function FilterPanel({ filters, onChange, proveedores = [], categorias = [] }) {
  const [open, setOpen] = useState(false);

  const updateFilter = (key, value) => {
    onChange({ ...filters, [key]: value });
  };

  const toggleEstado = (estado) => {
    const current = filters.estados || [];
    const next = current.includes(estado)
      ? current.filter((e) => e !== estado)
      : [...current, estado];
    updateFilter('estados', next);
  };

  const toggleCategoria = (cat) => {
    const current = filters.categorias || [];
    const next = current.includes(cat)
      ? current.filter((c) => c !== cat)
      : [...current, cat];
    updateFilter('categorias', next);
  };

  const clearFilters = () => {
    onChange({});
  };

  const hasFilters = Object.values(filters).some(
    (v) => v !== undefined && v !== null && v !== '' && (!Array.isArray(v) || v.length > 0)
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4" />
          <span>Filtros</span>
          {hasFilters && (
            <span className="bg-primary-100 text-primary-700 text-xs px-2 py-0.5 rounded-full">
              Activos
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="border-t border-gray-200 p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date range */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fecha desde</label>
              <input
                type="date"
                value={filters.fecha_desde || ''}
                onChange={(e) => updateFilter('fecha_desde', e.target.value)}
                className="input-field text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fecha hasta</label>
              <input
                type="date"
                value={filters.fecha_hasta || ''}
                onChange={(e) => updateFilter('fecha_hasta', e.target.value)}
                className="input-field text-sm"
              />
            </div>

            {/* Proveedor */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Proveedor</label>
              <select
                value={filters.proveedor_id || ''}
                onChange={(e) => updateFilter('proveedor_id', e.target.value)}
                className="input-field text-sm"
              >
                <option value="">Todos</option>
                {proveedores.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Origen */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Origen</label>
              <select
                value={filters.origen || ''}
                onChange={(e) => updateFilter('origen', e.target.value)}
                className="input-field text-sm"
              >
                <option value="">Todos</option>
                {ORIGENES.map((o) => (
                  <option key={o} value={o}>
                    {o.charAt(0).toUpperCase() + o.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Importe range */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Importe mín.</label>
              <input
                type="number"
                step="0.01"
                value={filters.importe_min || ''}
                onChange={(e) => updateFilter('importe_min', e.target.value)}
                placeholder="0.00"
                className="input-field text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Importe máx.</label>
              <input
                type="number"
                step="0.01"
                value={filters.importe_max || ''}
                onChange={(e) => updateFilter('importe_max', e.target.value)}
                placeholder="0.00"
                className="input-field text-sm"
              />
            </div>
          </div>

          {/* Estado chips */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Estado</label>
            <div className="flex flex-wrap gap-2">
              {ESTADOS.map((estado) => {
                const active = (filters.estados || []).includes(estado);
                return (
                  <button
                    key={estado}
                    onClick={() => toggleEstado(estado)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      active
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'
                    }`}
                  >
                    {estado.charAt(0).toUpperCase() + estado.slice(1).replace('_', ' ')}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Categoria chips */}
          {categorias.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Categoría</label>
              <div className="flex flex-wrap gap-2">
                {categorias.map((cat) => {
                  const active = (filters.categorias || []).includes(cat.id || cat.nombre);
                  return (
                    <button
                      key={cat.id || cat.nombre}
                      onClick={() => toggleCategoria(cat.id || cat.nombre)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'
                      }`}
                    >
                      {cat.nombre}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Clear filters */}
          {hasFilters && (
            <div className="flex justify-end">
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
              >
                <X className="w-4 h-4" />
                Limpiar filtros
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
